// "Operation notes" — what was done in theatre, read from the patient's record.
//
// The operative note used to exist ONLY inside the OT module. A surgeon seeing
// the patient in clinic six weeks later, or a physician admitting them for
// something else, had no way to read it: nothing outside main/ot referenced
// ot_case at all. So the note was written, certified, and then effectively
// filed somewhere nobody else could open.
//
// READ-ONLY, AND CERTIFIED ONLY. The server sends nothing that has not been
// signed off, because a draft shown here would read as the finished account of
// an operation. There is no edit affordance for the same reason — a note is
// corrected in OT by the person who wrote it, not from another department's
// screen.

import React, { useCallback, useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";

import { fetchPatientOperationNotes } from "../../ot/api/otApi";
import { formatIstDateTime } from "../../../utils/istDateTime";

const MUTED = "#64748B";
const BORDER = "#E2E8F0";

/** The note fields worth showing outside theatre, in the order a reader wants
 *  them: what was done, what was found, what was left behind, what went wrong. */
const SURGICAL_FIELDS = [
  ["procedure_performed", "Procedure performed"],
  ["findings", "Operative findings"],
  ["implants", "Implants"],
  ["specimens", "Specimens"],
  ["complications", "Complications"],
  ["post_op_instructions", "Post-op instructions"],
  ["vte_prophylaxis", "VTE prophylaxis"],
];

function Line({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, letterSpacing: "0.02em" }}>
        {label.toUpperCase()}
      </Typography>
      <Typography sx={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>{value}</Typography>
    </Box>
  );
}

function OperationCard({ record }) {
  const s = record.summary || {};
  const anaesthesia = record.anaesthesia;

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: "10px",
        p: 2,
        mb: 1.5,
        bgcolor: "#FFFFFF",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700 }}>
          {s.procedure_performed || record.procedure_name || "Operation"}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>
          {formatIstDateTime(record.performed_at) || "date not recorded"}
        </Typography>
        {s.wound_class && (
          <Chip
            size="small"
            label={`Wound: ${s.wound_class}`}
            sx={{ height: 22, fontSize: 11, border: `1px solid ${BORDER}` }}
            variant="outlined"
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: MUTED }}>{record.case_number}</Typography>
      </Box>

      <Typography sx={{ fontSize: 12.5, color: MUTED, mb: 1.5 }}>
        {record.primary_surgeon_name || "Surgeon not recorded"}
        {record.anaesthetist_name ? ` · anaesthetist ${record.anaesthetist_name}` : ""}
      </Typography>

      {SURGICAL_FIELDS.map(([key, label]) => (
        <Line key={key} label={label} value={s[key]} />
      ))}

      {/* The anaesthesia note is a second signature on the same operation. Only
          the parts a non-anaesthetist reads are surfaced; the full record stays
          in OT. */}
      {anaesthesia && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: MUTED, mb: 1 }}>
            ANAESTHESIA
          </Typography>
          <Line label="Technique" value={anaesthesia.anaesthesia_technique} />
          <Line label="ASA class" value={anaesthesia.asa_class} />
          <Line label="Complications" value={anaesthesia.complications} />
        </Box>
      )}
    </Box>
  );
}

export default function OperationNotesSection({ patientId, activeSection }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      setRecords(await fetchPatientOperationNotes(patientId));
    } catch (err) {
      // A patient with no theatre history is the common case and reads the
      // same as a failure here, so say which it was.
      toast.error(err?.message || "Could not load operation notes");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Only when the section is opened. Most consultations are not surgical, and
  // fetching theatre history for every patient on every console load would be
  // a request nobody reads.
  useEffect(() => {
    if (activeSection && records === null) load();
  }, [activeSection, records, load]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 13, color: MUTED }}>Loading operation notes…</Typography>
      </Box>
    );
  }

  if (!records || records.length === 0) {
    return (
      <Typography sx={{ fontSize: 13.5, color: MUTED, py: 1.5 }}>
        No certified operation notes for this patient. A note appears here once the
        surgeon has signed it in OT.
      </Typography>
    );
  }

  return (
    <Box>
      {records.map((r) => (
        <OperationCard key={r.ot_case_id} record={r} />
      ))}
    </Box>
  );
}
